/**
 * InstrumentPresets.js - Centralized preset library for all instruments
 *
 * Each instrument has an array of presets with:
 * - name: Display name
 * - data: Parameter values to apply
 */

// ============================================
// TB-303 PRESETS
// ============================================
export const TB303_PRESETS = [
    {
        name: 'Init',
        data: {
            waveform: 'sawtooth',
            cutoff: 1000,
            resonance: 10,
            envMod: 2000,
            decay: 0.2,
            accentLevel: 0.5,
            slideTime: 0.06,
            distortion: 0
        }
    },
    {
        name: 'Acid Squelch',
        data: {
            waveform: 'sawtooth',
            cutoff: 400,
            resonance: 18,
            envMod: 4000,
            decay: 0.15,
            accentLevel: 0.7,
            slideTime: 0.05,
            distortion: 0.3
        }
    },
    {
        name: 'Deep Bass',
        data: {
            waveform: 'square',
            cutoff: 300,
            resonance: 5,
            envMod: 1000,
            decay: 0.3,
            accentLevel: 0.4,
            slideTime: 0.08,
            distortion: 0.1
        }
    },
    {
        name: 'Resonant Lead',
        data: {
            waveform: 'sawtooth',
            cutoff: 800,
            resonance: 20,
            envMod: 3000,
            decay: 0.1,
            accentLevel: 0.8,
            slideTime: 0.04,
            distortion: 0.2
        }
    },
    {
        name: 'Zap Bass',
        data: {
            waveform: 'square',
            cutoff: 200,
            resonance: 15,
            envMod: 5000,
            decay: 0.08,
            accentLevel: 0.9,
            slideTime: 0.03,
            distortion: 0.4
        }
    },
    {
        name: 'Bubbles',
        data: {
            waveform: 'sawtooth',
            cutoff: 600,
            resonance: 22,
            envMod: 2500,
            decay: 0.25,
            accentLevel: 0.6,
            slideTime: 0.1,
            distortion: 0.15
        }
    },
    {
        name: 'Phuture',
        data: {
            waveform: 'sawtooth',
            cutoff: 350,
            resonance: 19,
            envMod: 4500,
            decay: 0.12,
            accentLevel: 0.85,
            slideTime: 0.06,
            distortion: 0.25
        }
    },
    {
        name: 'Mellow',
        data: {
            waveform: 'square',
            cutoff: 500,
            resonance: 8,
            envMod: 1500,
            decay: 0.35,
            accentLevel: 0.3,
            slideTime: 0.1,
            distortion: 0
        }
    }
];

// ============================================
// TR-808 PRESETS
// ============================================
export const TR808_PRESETS = [
    {
        name: 'Classic 808',
        data: {
            kickDecay: 0.5,
            kickTone: 50,
            snareSnappy: 0.5,
            snareDecay: 0.2,
            clapDecay: 0.15,
            hhDecay: 0.08,
            openHHDecay: 0.3,
            tomDecay: 0.25
        }
    },
    {
        name: 'Punchy',
        data: {
            kickDecay: 0.3,
            kickTone: 60,
            snareSnappy: 0.7,
            snareDecay: 0.15,
            clapDecay: 0.1,
            hhDecay: 0.05,
            openHHDecay: 0.2,
            tomDecay: 0.2
        }
    },
    {
        name: 'Boomy',
        data: {
            kickDecay: 0.8,
            kickTone: 40,
            snareSnappy: 0.3,
            snareDecay: 0.3,
            clapDecay: 0.2,
            hhDecay: 0.1,
            openHHDecay: 0.4,
            tomDecay: 0.35
        }
    },
    {
        name: 'Lo-Fi',
        data: {
            kickDecay: 0.4,
            kickTone: 45,
            snareSnappy: 0.4,
            snareDecay: 0.18,
            clapDecay: 0.12,
            hhDecay: 0.06,
            openHHDecay: 0.25,
            tomDecay: 0.22
        }
    },
    {
        name: 'Tight',
        data: {
            kickDecay: 0.25,
            kickTone: 55,
            snareSnappy: 0.6,
            snareDecay: 0.1,
            clapDecay: 0.08,
            hhDecay: 0.04,
            openHHDecay: 0.15,
            tomDecay: 0.15
        }
    },
    {
        name: 'Hip-Hop',
        data: {
            kickDecay: 0.6,
            kickTone: 48,
            snareSnappy: 0.55,
            snareDecay: 0.22,
            clapDecay: 0.18,
            hhDecay: 0.07,
            openHHDecay: 0.35,
            tomDecay: 0.28
        }
    }
];

// ============================================
// TR-909 PRESETS
// ============================================
export const TR909_PRESETS = [
    {
        name: 'Classic 909',
        data: {
            kickAttack: 0.5,
            kickDecay: 0.4,
            kickTune: 60,
            snareSnappy: 0.6,
            snareTune: 0.5,
            snareDecay: 0.2,
            clapDecay: 0.15,
            hhDecay: 0.06,
            openHHDecay: 0.25,
            rideDecay: 0.4,
            crashDecay: 0.8
        }
    },
    {
        name: 'Hard Techno',
        data: {
            kickAttack: 0.8,
            kickDecay: 0.3,
            kickTune: 65,
            snareSnappy: 0.8,
            snareTune: 0.6,
            snareDecay: 0.15,
            clapDecay: 0.1,
            hhDecay: 0.04,
            openHHDecay: 0.18,
            rideDecay: 0.3,
            crashDecay: 0.6
        }
    },
    {
        name: 'House',
        data: {
            kickAttack: 0.6,
            kickDecay: 0.45,
            kickTune: 55,
            snareSnappy: 0.5,
            snareTune: 0.45,
            snareDecay: 0.18,
            clapDecay: 0.12,
            hhDecay: 0.05,
            openHHDecay: 0.22,
            rideDecay: 0.35,
            crashDecay: 0.7
        }
    },
    {
        name: 'Electro',
        data: {
            kickAttack: 0.7,
            kickDecay: 0.35,
            kickTune: 70,
            snareSnappy: 0.7,
            snareTune: 0.55,
            snareDecay: 0.12,
            clapDecay: 0.08,
            hhDecay: 0.03,
            openHHDecay: 0.15,
            rideDecay: 0.25,
            crashDecay: 0.5
        }
    },
    {
        name: 'Trance',
        data: {
            kickAttack: 0.9,
            kickDecay: 0.38,
            kickTune: 62,
            snareSnappy: 0.65,
            snareTune: 0.5,
            snareDecay: 0.14,
            clapDecay: 0.1,
            hhDecay: 0.04,
            openHHDecay: 0.2,
            rideDecay: 0.32,
            crashDecay: 0.65
        }
    }
];

// ============================================
// MINIMOOG PRESETS
// ============================================
export const MINIMOOG_PRESETS = [
    {
        name: 'Init',
        data: {
            osc1Waveform: 'sawtooth', osc1Octave: 0, osc1Tune: 0, osc1Level: 0.8,
            osc2Waveform: 'sawtooth', osc2Octave: 0, osc2Tune: 5, osc2Level: 0.6,
            osc3Waveform: 'sawtooth', osc3Octave: 0, osc3Tune: -5, osc3Level: 0.4,
            noiseLevel: 0,
            cutoff: 2000, resonance: 2, filterEnvAmount: 0.5, filterKeyTrack: 0.5,
            filterAttack: 0.01, filterDecay: 0.3, filterSustain: 0.5,
            ampAttack: 0.01, ampDecay: 0.2, ampSustain: 0.7, ampRelease: 0.3,
            glide: 0, volume: 0.7
        }
    },
    {
        name: 'Fat Bass',
        data: {
            osc1Waveform: 'sawtooth', osc1Octave: -1, osc1Tune: 0, osc1Level: 1,
            osc2Waveform: 'square', osc2Octave: -1, osc2Tune: 3, osc2Level: 0.7,
            osc3Waveform: 'sawtooth', osc3Octave: -2, osc3Tune: 0, osc3Level: 0.5,
            noiseLevel: 0,
            cutoff: 400, resonance: 6, filterEnvAmount: 0.7, filterKeyTrack: 0.3,
            filterAttack: 0.001, filterDecay: 0.2, filterSustain: 0.2,
            ampAttack: 0.001, ampDecay: 0.3, ampSustain: 0.6, ampRelease: 0.2,
            glide: 0.03, volume: 0.8
        }
    },
    {
        name: 'Screaming Lead',
        data: {
            osc1Waveform: 'sawtooth', osc1Octave: 0, osc1Tune: 0, osc1Level: 0.9,
            osc2Waveform: 'sawtooth', osc2Octave: 0, osc2Tune: 7, osc2Level: 0.8,
            osc3Waveform: 'square', osc3Octave: 1, osc3Tune: 0, osc3Level: 0.3,
            noiseLevel: 0,
            cutoff: 1500, resonance: 10, filterEnvAmount: 0.6, filterKeyTrack: 0.6,
            filterAttack: 0.01, filterDecay: 0.15, filterSustain: 0.3,
            ampAttack: 0.01, ampDecay: 0.1, ampSustain: 0.8, ampRelease: 0.2,
            glide: 0.05, volume: 0.75
        }
    },
    {
        name: 'Growl',
        data: {
            osc1Waveform: 'sawtooth', osc1Octave: -1, osc1Tune: 0, osc1Level: 0.8,
            osc2Waveform: 'sawtooth', osc2Octave: 0, osc2Tune: 10, osc2Level: 0.7,
            osc3Waveform: 'square', osc3Octave: -1, osc3Tune: -8, osc3Level: 0.5,
            noiseLevel: 0.1,
            cutoff: 800, resonance: 8, filterEnvAmount: 0.5, filterKeyTrack: 0.4,
            filterAttack: 0.05, filterDecay: 0.4, filterSustain: 0.4,
            ampAttack: 0.02, ampDecay: 0.3, ampSustain: 0.7, ampRelease: 0.3,
            glide: 0.08, volume: 0.7
        }
    },
    {
        name: 'Lucky Man',
        data: {
            osc1Waveform: 'sawtooth', osc1Octave: 0, osc1Tune: 0, osc1Level: 0.7,
            osc2Waveform: 'sawtooth', osc2Octave: 0, osc2Tune: 5, osc2Level: 0.6,
            osc3Waveform: 'triangle', osc3Octave: -1, osc3Tune: 0, osc3Level: 0.4,
            noiseLevel: 0,
            cutoff: 1200, resonance: 4, filterEnvAmount: 0.4, filterKeyTrack: 0.5,
            filterAttack: 0.3, filterDecay: 0.8, filterSustain: 0.5,
            ampAttack: 0.2, ampDecay: 0.5, ampSustain: 0.8, ampRelease: 1,
            glide: 0.1, volume: 0.65
        }
    },
    {
        name: 'Sub Bass',
        data: {
            osc1Waveform: 'sine', osc1Octave: -2, osc1Tune: 0, osc1Level: 1,
            osc2Waveform: 'sine', osc2Octave: -1, osc2Tune: 0, osc2Level: 0.3,
            osc3Waveform: 'triangle', osc3Octave: -2, osc3Tune: 0, osc3Level: 0.5,
            noiseLevel: 0,
            cutoff: 200, resonance: 2, filterEnvAmount: 0.3, filterKeyTrack: 0.2,
            filterAttack: 0.01, filterDecay: 0.5, filterSustain: 0.6,
            ampAttack: 0.01, ampDecay: 0.4, ampSustain: 0.8, ampRelease: 0.25,
            glide: 0, volume: 0.9
        }
    }
];

// ============================================
// KORG MS-20 PRESETS
// ============================================
export const MS20_PRESETS = [
    {
        name: 'Init',
        data: {
            vco1Waveform: 'sawtooth', vco1Octave: 0, vco1Level: 0.8,
            vco2Waveform: 'sawtooth', vco2Octave: 0, vco2Level: 0.6, vco2Detune: 5,
            noiseLevel: 0, ringModLevel: 0,
            hpfCutoff: 20, hpfResonance: 0,
            lpfCutoff: 5000, lpfResonance: 2, lpfEnvAmount: 0.5, lpfKeyTrack: 0.5,
            eg1Attack: 0.01, eg1Decay: 0.3, eg1Sustain: 0.5, eg1Release: 0.3,
            eg2Attack: 0.01, eg2Decay: 0.2, eg2Sustain: 0.7, eg2Release: 0.3,
            mgRate: 5, mgPitchAmount: 0, mgFilterAmount: 0,
            portamento: 0, volume: 0.7
        }
    },
    {
        name: 'Industrial',
        data: {
            vco1Waveform: 'square', vco1Octave: 0, vco1Level: 0.9,
            vco2Waveform: 'sawtooth', vco2Octave: -1, vco2Level: 0.7, vco2Detune: 15,
            noiseLevel: 0.2, ringModLevel: 0.3,
            hpfCutoff: 200, hpfResonance: 5,
            lpfCutoff: 1500, lpfResonance: 12, lpfEnvAmount: 0.7, lpfKeyTrack: 0.4,
            eg1Attack: 0.001, eg1Decay: 0.1, eg1Sustain: 0.2, eg1Release: 0.1,
            eg2Attack: 0.001, eg2Decay: 0.15, eg2Sustain: 0.5, eg2Release: 0.15,
            mgRate: 8, mgPitchAmount: 0.1, mgFilterAmount: 0.3,
            portamento: 0, volume: 0.75
        }
    },
    {
        name: 'Scream',
        data: {
            vco1Waveform: 'sawtooth', vco1Octave: 0, vco1Level: 0.8,
            vco2Waveform: 'sawtooth', vco2Octave: 0, vco2Level: 0.8, vco2Detune: 8,
            noiseLevel: 0.1, ringModLevel: 0,
            hpfCutoff: 100, hpfResonance: 10,
            lpfCutoff: 2000, lpfResonance: 18, lpfEnvAmount: 0.8, lpfKeyTrack: 0.6,
            eg1Attack: 0.01, eg1Decay: 0.2, eg1Sustain: 0.3, eg1Release: 0.2,
            eg2Attack: 0.01, eg2Decay: 0.1, eg2Sustain: 0.7, eg2Release: 0.2,
            mgRate: 6, mgPitchAmount: 0.05, mgFilterAmount: 0,
            portamento: 0.03, volume: 0.7
        }
    },
    {
        name: 'Dark Bass',
        data: {
            vco1Waveform: 'square', vco1Octave: -1, vco1Level: 1,
            vco2Waveform: 'sawtooth', vco2Octave: -1, vco2Level: 0.6, vco2Detune: 3,
            noiseLevel: 0, ringModLevel: 0,
            hpfCutoff: 30, hpfResonance: 0,
            lpfCutoff: 500, lpfResonance: 8, lpfEnvAmount: 0.6, lpfKeyTrack: 0.3,
            eg1Attack: 0.001, eg1Decay: 0.25, eg1Sustain: 0.3, eg1Release: 0.2,
            eg2Attack: 0.001, eg2Decay: 0.3, eg2Sustain: 0.6, eg2Release: 0.25,
            mgRate: 0, mgPitchAmount: 0, mgFilterAmount: 0,
            portamento: 0.02, volume: 0.8
        }
    },
    {
        name: 'Experimental',
        data: {
            vco1Waveform: 'sawtooth', vco1Octave: 0, vco1Level: 0.6,
            vco2Waveform: 'square', vco2Octave: 1, vco2Level: 0.5, vco2Detune: 50,
            noiseLevel: 0.3, ringModLevel: 0.5,
            hpfCutoff: 300, hpfResonance: 8,
            lpfCutoff: 3000, lpfResonance: 15, lpfEnvAmount: 0.9, lpfKeyTrack: 0.5,
            eg1Attack: 0.5, eg1Decay: 1, eg1Sustain: 0.4, eg1Release: 1.5,
            eg2Attack: 0.3, eg2Decay: 0.8, eg2Sustain: 0.5, eg2Release: 1,
            mgRate: 3, mgPitchAmount: 0.2, mgFilterAmount: 0.4,
            portamento: 0.1, volume: 0.6
        }
    },
    {
        name: 'Sync Lead',
        data: {
            vco1Waveform: 'sawtooth', vco1Octave: 0, vco1Level: 0.7,
            vco2Waveform: 'sawtooth', vco2Octave: 0, vco2Level: 0.7, vco2Detune: 12,
            noiseLevel: 0, ringModLevel: 0.2,
            hpfCutoff: 80, hpfResonance: 3,
            lpfCutoff: 2500, lpfResonance: 6, lpfEnvAmount: 0.5, lpfKeyTrack: 0.6,
            eg1Attack: 0.01, eg1Decay: 0.2, eg1Sustain: 0.4, eg1Release: 0.25,
            eg2Attack: 0.01, eg2Decay: 0.15, eg2Sustain: 0.8, eg2Release: 0.2,
            mgRate: 5, mgPitchAmount: 0.03, mgFilterAmount: 0,
            portamento: 0.04, volume: 0.7
        }
    }
];

// ============================================
// JUPITER-8 PRESETS
// ============================================
export const JUPITER8_PRESETS = [
    {
        name: 'Init',
        data: {
            vco1Wave: 'sawtooth', vco1Octave: 0, vco1Level: 0.7,
            vco2Wave: 'sawtooth', vco2Octave: 0, vco2Level: 0.5, vco2Detune: 5,
            subLevel: 0.3, noiseLevel: 0,
            filterCutoff: 2000, filterResonance: 2, filterEnvAmount: 0.5, hpfCutoff: 20,
            filterAttack: 0.01, filterDecay: 0.3, filterSustain: 0.5, filterRelease: 0.5,
            ampAttack: 0.01, ampDecay: 0.2, ampSustain: 0.7, ampRelease: 0.5,
            lfoRate: 2, lfoToPitch: 0, lfoToFilter: 0,
            chorusRate: 0.8, chorusDepth: 0.5, chorusMix: 0.5,
            volume: 0.7, portamento: 0
        }
    },
    {
        name: 'Lush Pad',
        data: {
            vco1Wave: 'sawtooth', vco1Octave: 0, vco1Level: 0.6,
            vco2Wave: 'sawtooth', vco2Octave: 0, vco2Level: 0.6, vco2Detune: 8,
            subLevel: 0.2, noiseLevel: 0.05,
            filterCutoff: 1200, filterResonance: 3, filterEnvAmount: 0.3, hpfCutoff: 80,
            filterAttack: 0.8, filterDecay: 1.5, filterSustain: 0.6, filterRelease: 2,
            ampAttack: 0.5, ampDecay: 0.8, ampSustain: 0.8, ampRelease: 2.5,
            lfoRate: 0.3, lfoToPitch: 0.02, lfoToFilter: 0.1,
            chorusRate: 0.5, chorusDepth: 0.7, chorusMix: 0.6,
            volume: 0.7, portamento: 0.1
        }
    },
    {
        name: 'Fat Brass',
        data: {
            vco1Wave: 'sawtooth', vco1Octave: 0, vco1Level: 0.8,
            vco2Wave: 'sawtooth', vco2Octave: 0, vco2Level: 0.8, vco2Detune: 10,
            subLevel: 0.4, noiseLevel: 0,
            filterCutoff: 800, filterResonance: 4, filterEnvAmount: 0.7, hpfCutoff: 100,
            filterAttack: 0.05, filterDecay: 0.4, filterSustain: 0.3, filterRelease: 0.3,
            ampAttack: 0.02, ampDecay: 0.3, ampSustain: 0.6, ampRelease: 0.4,
            lfoRate: 5, lfoToPitch: 0.01, lfoToFilter: 0,
            chorusRate: 0.8, chorusDepth: 0.3, chorusMix: 0.3,
            volume: 0.7, portamento: 0
        }
    },
    {
        name: 'String Ensemble',
        data: {
            vco1Wave: 'sawtooth', vco1Octave: 0, vco1Level: 0.5,
            vco2Wave: 'sawtooth', vco2Octave: 1, vco2Level: 0.4, vco2Detune: 12,
            subLevel: 0.1, noiseLevel: 0.02,
            filterCutoff: 3000, filterResonance: 1, filterEnvAmount: 0.2, hpfCutoff: 200,
            filterAttack: 0.6, filterDecay: 0.8, filterSustain: 0.7, filterRelease: 1.5,
            ampAttack: 0.8, ampDecay: 0.5, ampSustain: 0.9, ampRelease: 2,
            lfoRate: 0.2, lfoToPitch: 0.015, lfoToFilter: 0.05,
            chorusRate: 0.6, chorusDepth: 0.8, chorusMix: 0.7,
            volume: 0.6, portamento: 0.05
        }
    },
    {
        name: 'Analog Lead',
        data: {
            vco1Wave: 'sawtooth', vco1Octave: 0, vco1Level: 0.7,
            vco2Wave: 'square', vco2Octave: 0, vco2Level: 0.5, vco2Detune: 3,
            subLevel: 0.5, noiseLevel: 0,
            filterCutoff: 1500, filterResonance: 6, filterEnvAmount: 0.6, hpfCutoff: 50,
            filterAttack: 0.01, filterDecay: 0.2, filterSustain: 0.4, filterRelease: 0.3,
            ampAttack: 0.01, ampDecay: 0.1, ampSustain: 0.8, ampRelease: 0.3,
            lfoRate: 6, lfoToPitch: 0.03, lfoToFilter: 0,
            chorusRate: 1, chorusDepth: 0.4, chorusMix: 0.4,
            volume: 0.7, portamento: 0.05
        }
    },
    {
        name: 'Duran Bass',
        data: {
            vco1Wave: 'sawtooth', vco1Octave: -1, vco1Level: 0.8,
            vco2Wave: 'square', vco2Octave: -1, vco2Level: 0.6, vco2Detune: 5,
            subLevel: 0.7, noiseLevel: 0,
            filterCutoff: 600, filterResonance: 5, filterEnvAmount: 0.5, hpfCutoff: 30,
            filterAttack: 0.01, filterDecay: 0.2, filterSustain: 0.3, filterRelease: 0.2,
            ampAttack: 0.01, ampDecay: 0.15, ampSustain: 0.7, ampRelease: 0.2,
            lfoRate: 0, lfoToPitch: 0, lfoToFilter: 0,
            chorusRate: 0.8, chorusDepth: 0.4, chorusMix: 0.5,
            volume: 0.75, portamento: 0.03
        }
    }
];

// ============================================
// JUNO-106 PRESETS
// ============================================
export const JUNO106_PRESETS = [
    {
        name: 'Init',
        data: {
            dcoSaw: true, dcoPulse: false, dcoPulseWidth: 0.5, dcoOctave: 0,
            subLevel: 0.3, noiseLevel: 0, hpfFreq: 0,
            lpfCutoff: 3000, lpfResonance: 1, lpfEnvAmount: 0.5, lpfKeyTrack: 0.5,
            envAttack: 0.01, envDecay: 0.3, envSustain: 0.6, envRelease: 0.4,
            lfoRate: 1, lfoToPitch: 0, lfoToFilter: 0, lfoToPW: 0,
            chorusMode: 2, volume: 0.7
        }
    },
    {
        name: 'Classic Pad',
        data: {
            dcoSaw: true, dcoPulse: true, dcoPulseWidth: 0.5, dcoOctave: 0,
            subLevel: 0.2, noiseLevel: 0.02, hpfFreq: 1,
            lpfCutoff: 1500, lpfResonance: 2, lpfEnvAmount: 0.3, lpfKeyTrack: 0.5,
            envAttack: 0.6, envDecay: 1, envSustain: 0.7, envRelease: 1.5,
            lfoRate: 0.3, lfoToPitch: 0.02, lfoToFilter: 0.1, lfoToPW: 0.2,
            chorusMode: 3, volume: 0.6
        }
    },
    {
        name: 'Italo Bass',
        data: {
            dcoSaw: true, dcoPulse: false, dcoPulseWidth: 0.5, dcoOctave: -1,
            subLevel: 0.5, noiseLevel: 0, hpfFreq: 0,
            lpfCutoff: 800, lpfResonance: 4, lpfEnvAmount: 0.6, lpfKeyTrack: 0.3,
            envAttack: 0.01, envDecay: 0.2, envSustain: 0.3, envRelease: 0.2,
            lfoRate: 0, lfoToPitch: 0, lfoToFilter: 0, lfoToPW: 0,
            chorusMode: 1, volume: 0.8
        }
    },
    {
        name: 'Synthwave Lead',
        data: {
            dcoSaw: true, dcoPulse: false, dcoPulseWidth: 0.5, dcoOctave: 0,
            subLevel: 0.4, noiseLevel: 0, hpfFreq: 0,
            lpfCutoff: 2000, lpfResonance: 5, lpfEnvAmount: 0.5, lpfKeyTrack: 0.6,
            envAttack: 0.01, envDecay: 0.3, envSustain: 0.5, envRelease: 0.4,
            lfoRate: 5, lfoToPitch: 0.05, lfoToFilter: 0, lfoToPW: 0,
            chorusMode: 2, volume: 0.7
        }
    },
    {
        name: 'House Stab',
        data: {
            dcoSaw: false, dcoPulse: true, dcoPulseWidth: 0.5, dcoOctave: 0,
            subLevel: 0, noiseLevel: 0, hpfFreq: 2,
            lpfCutoff: 1200, lpfResonance: 6, lpfEnvAmount: 0.7, lpfKeyTrack: 0.5,
            envAttack: 0.01, envDecay: 0.15, envSustain: 0.1, envRelease: 0.1,
            lfoRate: 0, lfoToPitch: 0, lfoToFilter: 0, lfoToPW: 0,
            chorusMode: 0, volume: 0.8
        }
    },
    {
        name: 'Dreamy Pad',
        data: {
            dcoSaw: true, dcoPulse: true, dcoPulseWidth: 0.4, dcoOctave: 0,
            subLevel: 0.15, noiseLevel: 0.03, hpfFreq: 1,
            lpfCutoff: 2500, lpfResonance: 2, lpfEnvAmount: 0.2, lpfKeyTrack: 0.4,
            envAttack: 1.2, envDecay: 1.5, envSustain: 0.8, envRelease: 2.5,
            lfoRate: 0.2, lfoToPitch: 0.01, lfoToFilter: 0.15, lfoToPW: 0.3,
            chorusMode: 3, volume: 0.55
        }
    },
    {
        name: 'PWM Strings',
        data: {
            dcoSaw: false, dcoPulse: true, dcoPulseWidth: 0.5, dcoOctave: 0,
            subLevel: 0.1, noiseLevel: 0.01, hpfFreq: 1,
            lpfCutoff: 4000, lpfResonance: 1, lpfEnvAmount: 0.15, lpfKeyTrack: 0.5,
            envAttack: 0.8, envDecay: 0.8, envSustain: 0.85, envRelease: 1.8,
            lfoRate: 1.5, lfoToPitch: 0, lfoToFilter: 0.05, lfoToPW: 0.6,
            chorusMode: 3, volume: 0.6
        }
    },
    {
        name: 'Funk Bass',
        data: {
            dcoSaw: true, dcoPulse: false, dcoPulseWidth: 0.5, dcoOctave: -1,
            subLevel: 0.6, noiseLevel: 0, hpfFreq: 0,
            lpfCutoff: 600, lpfResonance: 8, lpfEnvAmount: 0.8, lpfKeyTrack: 0.2,
            envAttack: 0.001, envDecay: 0.12, envSustain: 0.15, envRelease: 0.15,
            lfoRate: 0, lfoToPitch: 0, lfoToFilter: 0, lfoToPW: 0,
            chorusMode: 0, volume: 0.85
        }
    },
    {
        name: 'Soft Brass',
        data: {
            dcoSaw: true, dcoPulse: true, dcoPulseWidth: 0.4, dcoOctave: 0,
            subLevel: 0.3, noiseLevel: 0, hpfFreq: 1,
            lpfCutoff: 1000, lpfResonance: 3, lpfEnvAmount: 0.6, lpfKeyTrack: 0.5,
            envAttack: 0.08, envDecay: 0.4, envSustain: 0.4, envRelease: 0.35,
            lfoRate: 4, lfoToPitch: 0.02, lfoToFilter: 0, lfoToPW: 0,
            chorusMode: 2, volume: 0.7
        }
    }
];

// ============================================
// SH-101 PRESETS
// ============================================
export const SH101_PRESETS = [
    {
        name: 'Init',
        data: {
            vcoSaw: true, vcoPulse: false, vcoPulseWidth: 0.5, vcoOctave: 0,
            sub1Level: 0, sub2Level: 0, noiseLevel: 0,
            lpfCutoff: 2000, lpfResonance: 1, lpfEnvAmount: 0.5, lpfKeyTrack: 0.5,
            envAttack: 0.01, envDecay: 0.3, envSustain: 0.5, envRelease: 0.4,
            lfoRate: 5, lfoWave: 'triangle', lfoToPitch: 0, lfoToFilter: 0, lfoToPW: 0,
            portamento: 0, volume: 0.7
        }
    },
    {
        name: 'Acid Bass',
        data: {
            vcoSaw: true, vcoPulse: false, vcoPulseWidth: 0.5, vcoOctave: -1,
            sub1Level: 0.4, sub2Level: 0, noiseLevel: 0,
            lpfCutoff: 500, lpfResonance: 12, lpfEnvAmount: 0.8, lpfKeyTrack: 0.3,
            envAttack: 0.001, envDecay: 0.15, envSustain: 0.1, envRelease: 0.1,
            lfoRate: 0, lfoWave: 'triangle', lfoToPitch: 0, lfoToFilter: 0, lfoToPW: 0,
            portamento: 0.03, volume: 0.8
        }
    },
    {
        name: 'Hoover',
        data: {
            vcoSaw: true, vcoPulse: true, vcoPulseWidth: 0.3, vcoOctave: -1,
            sub1Level: 0.5, sub2Level: 0.3, noiseLevel: 0.05,
            lpfCutoff: 1200, lpfResonance: 4, lpfEnvAmount: 0.4, lpfKeyTrack: 0.5,
            envAttack: 0.2, envDecay: 0.5, envSustain: 0.6, envRelease: 0.8,
            lfoRate: 3, lfoWave: 'triangle', lfoToPitch: 0.1, lfoToFilter: 0.15, lfoToPW: 0.3,
            portamento: 0.15, volume: 0.7
        }
    },
    {
        name: 'Punchy Lead',
        data: {
            vcoSaw: true, vcoPulse: false, vcoPulseWidth: 0.5, vcoOctave: 0,
            sub1Level: 0.3, sub2Level: 0, noiseLevel: 0,
            lpfCutoff: 1500, lpfResonance: 6, lpfEnvAmount: 0.6, lpfKeyTrack: 0.6,
            envAttack: 0.01, envDecay: 0.2, envSustain: 0.4, envRelease: 0.3,
            lfoRate: 5, lfoWave: 'triangle', lfoToPitch: 0.04, lfoToFilter: 0, lfoToPW: 0,
            portamento: 0.05, volume: 0.75
        }
    },
    {
        name: 'Sub Bass',
        data: {
            vcoSaw: false, vcoPulse: true, vcoPulseWidth: 0.5, vcoOctave: -2,
            sub1Level: 0.8, sub2Level: 0.5, noiseLevel: 0,
            lpfCutoff: 300, lpfResonance: 2, lpfEnvAmount: 0.3, lpfKeyTrack: 0.2,
            envAttack: 0.01, envDecay: 0.4, envSustain: 0.7, envRelease: 0.3,
            lfoRate: 0, lfoWave: 'triangle', lfoToPitch: 0, lfoToFilter: 0, lfoToPW: 0,
            portamento: 0, volume: 0.9
        }
    },
    {
        name: 'Techno Stab',
        data: {
            vcoSaw: true, vcoPulse: true, vcoPulseWidth: 0.4, vcoOctave: 0,
            sub1Level: 0, sub2Level: 0, noiseLevel: 0,
            lpfCutoff: 800, lpfResonance: 8, lpfEnvAmount: 0.7, lpfKeyTrack: 0.5,
            envAttack: 0.001, envDecay: 0.08, envSustain: 0, envRelease: 0.05,
            lfoRate: 0, lfoWave: 'triangle', lfoToPitch: 0, lfoToFilter: 0, lfoToPW: 0,
            portamento: 0, volume: 0.8
        }
    },
    {
        name: 'Rave Lead',
        data: {
            vcoSaw: true, vcoPulse: false, vcoPulseWidth: 0.5, vcoOctave: 1,
            sub1Level: 0.2, sub2Level: 0, noiseLevel: 0,
            lpfCutoff: 3000, lpfResonance: 5, lpfEnvAmount: 0.5, lpfKeyTrack: 0.7,
            envAttack: 0.01, envDecay: 0.15, envSustain: 0.6, envRelease: 0.2,
            lfoRate: 6, lfoWave: 'triangle', lfoToPitch: 0.06, lfoToFilter: 0.1, lfoToPW: 0,
            portamento: 0.02, volume: 0.7
        }
    },
    {
        name: 'Wobble Bass',
        data: {
            vcoSaw: true, vcoPulse: false, vcoPulseWidth: 0.5, vcoOctave: -1,
            sub1Level: 0.6, sub2Level: 0.3, noiseLevel: 0,
            lpfCutoff: 400, lpfResonance: 10, lpfEnvAmount: 0.2, lpfKeyTrack: 0.3,
            envAttack: 0.01, envDecay: 0.3, envSustain: 0.6, envRelease: 0.3,
            lfoRate: 4, lfoWave: 'triangle', lfoToPitch: 0, lfoToFilter: 0.7, lfoToPW: 0,
            portamento: 0, volume: 0.8
        }
    }
];

// ============================================
// ARP 2600 PRESETS
// ============================================
export const ARP2600_PRESETS = [
    {
        name: 'Init',
        data: {
            vco1Wave: 'sawtooth', vco1Fine: 0, vco1Level: 0.8,
            vco2Wave: 'sawtooth', vco2Fine: 0, vco2Level: 0.6,
            vco3Wave: 'sawtooth', vco3Fine: 0, vco3Level: 0.4,
            noiseLevel: 0, ringModLevel: 0,
            filterCutoff: 5000, filterResonance: 1, filterEnvAmount: 0.5,
            env1Attack: 0.01, env1Decay: 0.3, env1Sustain: 0.5, env1Release: 0.5,
            env2Attack: 0.01, env2Decay: 0.2, env2Sustain: 0.7, env2Release: 0.3,
            lfoRate: 5, lfoWave: 'triangle', lfoToVco: 0, lfoToFilter: 0, lfoToVca: 0,
            volume: 0.7, portamento: 0
        }
    },
    {
        name: 'Classic Lead',
        data: {
            vco1Wave: 'sawtooth', vco1Fine: 0, vco1Level: 0.9,
            vco2Wave: 'sawtooth', vco2Fine: 7, vco2Level: 0.7,
            vco3Wave: 'square', vco3Fine: -12, vco3Level: 0.3,
            noiseLevel: 0, ringModLevel: 0,
            filterCutoff: 2500, filterResonance: 4, filterEnvAmount: 0.6,
            env1Attack: 0.01, env1Decay: 0.4, env1Sustain: 0.3, env1Release: 0.3,
            env2Attack: 0.01, env2Decay: 0.1, env2Sustain: 0.8, env2Release: 0.2,
            lfoRate: 6, lfoWave: 'triangle', lfoToVco: 0.02, lfoToFilter: 0, lfoToVca: 0,
            volume: 0.75, portamento: 0.05
        }
    },
    {
        name: 'Funky Bass',
        data: {
            vco1Wave: 'square', vco1Fine: 0, vco1Level: 1,
            vco2Wave: 'sawtooth', vco2Fine: -5, vco2Level: 0.5,
            vco3Wave: 'square', vco3Fine: 0, vco3Level: 0,
            noiseLevel: 0, ringModLevel: 0,
            filterCutoff: 800, filterResonance: 6, filterEnvAmount: 0.8,
            env1Attack: 0.001, env1Decay: 0.15, env1Sustain: 0.1, env1Release: 0.1,
            env2Attack: 0.001, env2Decay: 0.3, env2Sustain: 0.6, env2Release: 0.15,
            lfoRate: 0, lfoWave: 'triangle', lfoToVco: 0, lfoToFilter: 0, lfoToVca: 0,
            volume: 0.8, portamento: 0.02
        }
    },
    {
        name: 'Sci-Fi',
        data: {
            vco1Wave: 'sawtooth', vco1Fine: 0, vco1Level: 0.6,
            vco2Wave: 'square', vco2Fine: 70, vco2Level: 0.5,
            vco3Wave: 'sine', vco3Fine: 0, vco3Level: 0.3,
            noiseLevel: 0.2, ringModLevel: 0.4,
            filterCutoff: 3000, filterResonance: 12, filterEnvAmount: 0.7,
            env1Attack: 0.5, env1Decay: 1, env1Sustain: 0.2, env1Release: 1.5,
            env2Attack: 0.3, env2Decay: 0.5, env2Sustain: 0.5, env2Release: 1,
            lfoRate: 3, lfoWave: 'sine', lfoToVco: 0.1, lfoToFilter: 0.3, lfoToVca: 0,
            volume: 0.6, portamento: 0.1
        }
    },
    {
        name: 'Pad Sweep',
        data: {
            vco1Wave: 'sawtooth', vco1Fine: 0, vco1Level: 0.5,
            vco2Wave: 'sawtooth', vco2Fine: 12, vco2Level: 0.5,
            vco3Wave: 'triangle', vco3Fine: -12, vco3Level: 0.3,
            noiseLevel: 0.05, ringModLevel: 0,
            filterCutoff: 1500, filterResonance: 3, filterEnvAmount: 0.4,
            env1Attack: 1, env1Decay: 1.5, env1Sustain: 0.6, env1Release: 2,
            env2Attack: 0.8, env2Decay: 0.5, env2Sustain: 0.8, env2Release: 2.5,
            lfoRate: 0.5, lfoWave: 'triangle', lfoToVco: 0.01, lfoToFilter: 0.15, lfoToVca: 0,
            volume: 0.65, portamento: 0.2
        }
    },
    {
        name: 'R2-D2',
        data: {
            vco1Wave: 'sine', vco1Fine: 0, vco1Level: 0.8,
            vco2Wave: 'square', vco2Fine: 50, vco2Level: 0.4,
            vco3Wave: 'sine', vco3Fine: 0, vco3Level: 0,
            noiseLevel: 0.1, ringModLevel: 0.3,
            filterCutoff: 4000, filterResonance: 8, filterEnvAmount: 0.9,
            env1Attack: 0.001, env1Decay: 0.08, env1Sustain: 0, env1Release: 0.05,
            env2Attack: 0.001, env2Decay: 0.1, env2Sustain: 0, env2Release: 0.05,
            lfoRate: 15, lfoWave: 'square', lfoToVco: 0.3, lfoToFilter: 0.5, lfoToVca: 0,
            volume: 0.7, portamento: 0
        }
    }
];

// ============================================
// THEREMIN PRESETS
// ============================================
export const THEREMIN_PRESETS = [
    {
        name: 'Classic',
        data: {
            waveform: 'sine',
            vibrato: 5,
            vibratoAmount: 0.3,
            portamento: 0.05,
            volume: 0.6
        }
    },
    {
        name: 'Spooky',
        data: {
            waveform: 'triangle',
            vibrato: 3,
            vibratoAmount: 0.6,
            portamento: 0.15,
            volume: 0.5
        }
    },
    {
        name: 'Sci-Fi',
        data: {
            waveform: 'sawtooth',
            vibrato: 8,
            vibratoAmount: 0.2,
            portamento: 0.02,
            volume: 0.7
        }
    },
    {
        name: 'Ethereal',
        data: {
            waveform: 'sine',
            vibrato: 2,
            vibratoAmount: 0.5,
            portamento: 0.2,
            volume: 0.4
        }
    }
];

// ============================================
// EXPORT ALL PRESETS
// ============================================
export const ALL_PRESETS = {
    tb303: TB303_PRESETS,
    tr808: TR808_PRESETS,
    tr909: TR909_PRESETS,
    minimoog: MINIMOOG_PRESETS,
    ms20: MS20_PRESETS,
    jupiter8: JUPITER8_PRESETS,
    juno106: JUNO106_PRESETS,
    sh101: SH101_PRESETS,
    arp2600: ARP2600_PRESETS,
    theremin: THEREMIN_PRESETS
};

/**
 * Get presets for a specific instrument
 * @param {string} instrumentId - Instrument identifier
 * @returns {Array} Array of preset objects
 */
export function getPresetsFor(instrumentId) {
    return ALL_PRESETS[instrumentId] || [];
}

/**
 * Get a specific preset by name
 * @param {string} instrumentId - Instrument identifier
 * @param {string} presetName - Preset name
 * @returns {Object|null} Preset data or null
 */
export function getPreset(instrumentId, presetName) {
    const presets = ALL_PRESETS[instrumentId];
    if (!presets) return null;
    const preset = presets.find(p => p.name === presetName);
    return preset ? preset.data : null;
}

export default ALL_PRESETS;
